import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { PropertyStructure, PropertyType, Unit } from "@/types/types";
import { useAuth } from "@/components/auth-provider";
import { toast } from "sonner";
import { addProperty } from "@/lib/property-service";
import { sanitizePropertyData } from "@/lib/sanitize";

// Step components
import PropertyTypeSelector from "@/pages/landlord/wizard-steps/PropertyTypeSelector";
import BasicInfoForm from "@/pages/landlord/wizard-steps/BasicInfoForm";
import SingleUnitDetailsForm from "@/pages/landlord/wizard-steps/SingleUnitDetailsForm";
import MultiUnitDetailsForm from "@/pages/landlord/wizard-steps/MultiUnitDetailsForm";
import PhotosDescriptionForm from "@/pages/landlord/wizard-steps/PhotosDescriptionForm";

interface PropertyDraft {
  // Step 1: Property Structure
  structure?: PropertyStructure;

  // Step 2: Basic Info
  title?: string;
  address?: string;
  type?: PropertyType;

  // Step 3A: Single Unit Details
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  rent?: number;

  // Step 3B: Building Details (multi-unit)
  totalFloors?: number;
  totalUnits?: number;
  buildingFacilities?: string[];

  // Step 3C: Units
  units?: Partial<Unit>[];

  // Step 4: Amenities
  amenities?: string[];

  // Step 5: Photos
  images?: string[];
  coverImage?: string;

  // Step 6: Description
  description?: string;

  // Step 7: Lease Terms
  leaseDuration?: number;
  securityDeposit?: number;
  paymentTerms?: string;

  // Metadata
  lastSaved?: string;
}

const STEPS = [
  {
    number: 1,
    title: "Property Type & Info",
    description: "Type, name, location",
  },
  {
    number: 2,
    title: "Details & Amenities",
    description: "Specs, units, features",
  },
  {
    number: 3,
    title: "Photos & Description",
    description: "Images, details, terms",
  },
];

export default function PropertyAddWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [draft, setDraft] = useState<PropertyDraft>({});
  const [isSaving, setIsSaving] = useState(false);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    const autoSave = setInterval(() => {
      saveDraft();
    }, 30000); // 30 seconds

    return () => clearInterval(autoSave);
  }, [draft]);

  // Load draft from localStorage on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem("property-draft");
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        setDraft(parsed);
        toast.info("Draft loaded", {
          description: "Your previous work has been restored.",
        });
      } catch (error) {
        console.error("Failed to load draft:", error);
      }
    }
  }, []);

  const saveDraft = () => {
    const draftWithTimestamp = {
      ...draft,
      lastSaved: new Date().toISOString(),
    };
    localStorage.setItem("property-draft", JSON.stringify(draftWithTimestamp));
    setIsSaving(true);
    setTimeout(() => setIsSaving(false), 1000);
  };

  const updateDraft = (updates: Partial<PropertyDraft>) => {
    setDraft((prev) => ({ ...prev, ...updates }));
  };

  const clearDraft = () => {
    localStorage.removeItem("property-draft");
    setDraft({});
  };

  const canProceedToNextStep = (): boolean => {
    switch (currentStep) {
      case 1:
        // Step 1: Property Type & Basic Info
        return !!(
          draft.structure &&
          draft.title &&
          draft.address &&
          draft.type
        );
      case 2:
        // Step 2: Details & Amenities
        if (draft.structure === "single-unit") {
          return !!(
            draft.bedrooms &&
            draft.bathrooms &&
            draft.area &&
            draft.rent
          );
        } else {
          return !!(
            draft.totalFloors &&
            draft.totalUnits &&
            draft.units &&
            draft.units.length > 0
          );
        }
      case 3:
        // Step 3: Photos & Description
        // Check that images are uploaded (Firebase Storage URLs, not data URLs)
        const hasValidImages =
          draft.images &&
          draft.images.length > 0 &&
          draft.images.every((url) => url.startsWith("http"));

        return !!(
          hasValidImages &&
          draft.description &&
          draft.description.length >= 50 &&
          draft.leaseDuration &&
          draft.securityDeposit &&
          draft.paymentTerms
        );
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (canProceedToNextStep() && currentStep < 3) {
      setCurrentStep((prev) => prev + 1);
      saveDraft();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (!canProceedToNextStep()) {
      toast.error("Please complete all required fields");
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error("You must be logged in to submit a property");
      return;
    }

    if (!canProceedToNextStep()) {
      toast.error("Please complete all required fields");
      return;
    }

    setIsSaving(true);
    try {
      // Validate that images are Firebase Storage URLs, not data URLs
      const hasDataUrls = draft.images?.some((url) => url.startsWith("data:"));
      if (hasDataUrls) {
        toast.error("Please wait for all images to finish uploading");
        setIsSaving(false);
        return;
      }

      // Filter out any data URLs from images (safety check)
      const validImageUrls = (draft.images || []).filter((url) =>
        url.startsWith("http")
      );

      if (
        validImageUrls.length === 0 &&
        draft.images &&
        draft.images.length > 0
      ) {
        toast.error(
          "Image upload incomplete. Please try uploading images again."
        );
        setIsSaving(false);
        return;
      }

      // Prepare base property data
      const basePropertyData = {
        title: draft.title!,
        address: draft.address!,
        type: draft.type!,
        structure: draft.structure!,
        status: "vacant" as const,
        approvalStatus: "pending" as const,
        ownerId: user.id,
        listerId: user.id,
        isAgentListing: false,
        description: draft.description,
        amenities: draft.amenities || [],
        imageUrls: validImageUrls,
        image: draft.coverImage?.startsWith("http")
          ? draft.coverImage
          : validImageUrls[0],
        leaseDuration: draft.leaseDuration,
        securityDeposit: draft.securityDeposit,
        paymentTerms: draft.paymentTerms,
      };

      // Add structure-specific fields and ensure required fields are present
      let propertyData;
      if (draft.structure === "single-unit") {
        propertyData = {
          ...basePropertyData,
          bedrooms: draft.bedrooms!,
          bathrooms: draft.bathrooms!,
          area: draft.area!,
          rent: draft.rent!,
        };
      } else {
        propertyData = {
          ...basePropertyData,
          totalFloors: draft.totalFloors!,
          totalUnits: draft.totalUnits!,
          buildingFacilities: draft.buildingFacilities || [],
          units: draft.units || [],
          // Use first unit's details or defaults for building-level requirements
          rent: draft.units?.[0]?.rent || 0,
          bedrooms: draft.units?.[0]?.bedrooms || 0,
          bathrooms: draft.units?.[0]?.bathrooms || 0,
          area: draft.units?.[0]?.area || 0,
        };
      }

      // Sanitize property data before submission
      const sanitizedData = sanitizePropertyData(propertyData);
      await addProperty(sanitizedData);

      toast.success("Property submitted for approval!", {
        description: "An admin will review your listing shortly.",
      });

      clearDraft();
      navigate("/landlord/properties");
    } catch (error) {
      console.error("Error submitting property:", error);
      toast.error("Failed to submit property", {
        description:
          error instanceof Error
            ? error.message
            : "Please try again or contact support.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // Combined: Property Type & Basic Info
        return (
          <div className="space-y-8">
            <PropertyTypeSelector
              value={draft.structure}
              onChange={(structure) => updateDraft({ structure })}
            />
            {draft.structure && (
              <div className="pt-8 border-t border-gray-200">
                <BasicInfoForm
                  values={{
                    title: draft.title,
                    address: draft.address,
                    type: draft.type,
                  }}
                  onChange={(values: Partial<PropertyDraft>) =>
                    updateDraft(values)
                  }
                />
              </div>
            )}
          </div>
        );
      case 2:
        // Details & Amenities (conditional on structure)
        if (draft.structure === "single-unit") {
          return (
            <SingleUnitDetailsForm
              values={{
                bedrooms: draft.bedrooms,
                bathrooms: draft.bathrooms,
                area: draft.area,
                rent: draft.rent,
                amenities: draft.amenities,
              }}
              onChange={(values) => updateDraft(values)}
            />
          );
        } else if (draft.structure === "multi-unit") {
          return (
            <MultiUnitDetailsForm
              values={{
                totalFloors: draft.totalFloors,
                totalUnits: draft.totalUnits,
                buildingFacilities: draft.buildingFacilities,
                units: draft.units,
              }}
              onChange={(values) => updateDraft(values)}
            />
          );
        }
        return (
          <div className="text-center py-12 text-muted-foreground">
            Please select a property structure first
          </div>
        );
      case 3:
        // Photos & Description
        return (
          <PhotosDescriptionForm
            values={{
              images: draft.images,
              coverImage: draft.coverImage,
              description: draft.description,
              leaseDuration: draft.leaseDuration,
              securityDeposit: draft.securityDeposit,
              paymentTerms: draft.paymentTerms,
            }}
            onChange={(values) => updateDraft(values)}
          />
        );
      default:
        return (
          <div className="text-center py-12 text-muted-foreground">
            Step {currentStep} - Coming soon
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/landlord/dashboard/properties")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <div className="h-6 w-px bg-border" />
              <h1 className="text-2xl font-semibold text-foreground">
                Add New Property
              </h1>
            </div>
            {isSaving && (
              <span className="text-sm text-muted-foreground">
                Draft saved...
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="bg-card border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      currentStep === step.number
                        ? "bg-[#007AFF] text-white shadow-lg"
                        : currentStep > step.number
                        ? "bg-green-500 text-white"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {currentStep > step.number ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      step.number
                    )}
                  </div>
                  <div className="mt-2 text-center hidden sm:block">
                    <div
                      className={`text-sm font-medium ${
                        currentStep >= step.number
                          ? "text-foreground"
                          : "text-muted-foreground"
                      }`}
                    >
                      {step.title}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {step.description}
                    </div>
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`w-8 sm:w-16 h-1 mx-1 sm:mx-2 transition-all ${
                      currentStep > step.number ? "bg-green-500" : "bg-border"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 sm:hidden text-center">
            <div className="text-lg font-semibold text-foreground">
              {STEPS[currentStep - 1].title}
            </div>
            <div className="text-sm text-muted-foreground">
              Step {currentStep} of {STEPS.length}
            </div>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="p-6 sm:p-8 shadow-md">{renderStepContent()}</Card>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="px-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>

          {currentStep < 3 ? (
            <Button
              onClick={nextStep}
              disabled={!canProceedToNextStep()}
              className="px-6 bg-[#007AFF] hover:bg-[#0051D5]"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-8 bg-green-600 hover:bg-green-700"
            >
              {isSaving ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Submit for Approval
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
